import { format_date, format_time } from "helpers/datetime_helper"
import { StatusTimeline } from "components/Core"
import CollapseWithDetail from "components/Core/Collapse/WithDetail"
import { 
    HiX, 
    HiArrowLeft,
    HiOutlineClipboardList, 
    HiOutlineCube, 
    HiOutlineNewspaper, 
    HiOutlineTicket, 
    HiOutlineDocumentText 
} from "react-icons/hi"
import cn from "classnames"
import translatesObj from "overrides/theme/entel/base/translates/translation.json"

interface SgModalViewProps {
  sg: any
  onClose: () => void
}

const SgModalView = ({ sg, onClose }: SgModalViewProps) => {
  if (!sg) return null

  const statusses: any = translatesObj

  const _docs = {
    _dte: sg?.dte?.url || null,
    _summary: sg?.enviameLabel?.length ? sg?.enviameLabel[0]?.summary : null,
    _labels: sg?.enviameLabel?.length ? sg?.enviameLabel[0]?.labels : null,
  }

  const courierName = sg?.logisticPlan?.route ? sg?.logisticPlan?.route[0]?.courier?.name : ""
  const currentState = sg?._omnixStateMachine?.currentState?.state?.name || ""
  const stateTitle = statusses[currentState]?.title || currentState

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 sm:p-6 backdrop-blur-sm">
      <div className="bg-gray-50 rounded-2xl shadow-xl w-full max-w-5xl max-h-full flex flex-col overflow-hidden animate-fade-in-up">
        {/* Header */}
        <div className="px-6 py-5 bg-white border-b border-gray-200 flex items-center justify-between sticky top-0 z-20 backdrop-blur-md bg-white/80">
          <div className="flex flex-row items-center gap-5">
            <button
              onClick={onClose}
              className="w-11 h-11 rounded-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-lg active:scale-90 border border-blue-500/30"
              title="Volver"
            >
              <HiArrowLeft className="text-xl" />
            </button>
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 uppercase tracking-widest leading-none">
                   Quick View
                </span>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <span className="text-[11px] font-bold text-gray-400">ID: {sg.id}</span>
              </div>
              <h3 className="text-[19px] font-black text-gray-800 leading-tight">
                Detalle del Grupo de Envíos
              </h3>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
             <div className="hidden sm:flex flex-col items-end mr-2">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Orden Asociada</span>
                <span className="text-[14px] font-bold text-gray-700">{sg?.orderId}</span>
             </div>
             <button
               onClick={onClose}
               className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500 transition-all active:scale-95"
             >
               <HiX className="text-xl" />
             </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto w-full">
          <div className="w-full flex flex-col items-start justify-start pb-10">
            {/* Timeline */}
            <div className="w-full h-auto py-8 px-8 bg-white border-b border-gray-100 mb-8 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                   <div className="w-2 h-6 bg-blue-600 rounded-full"></div>
                   <h4 className="text-[14px] font-black text-gray-800 uppercase tracking-widest">Historial de Estados</h4>
                </div>
              <StatusTimeline statuses={sg?._omnixStateMachine?.states} />
            </div>

            {/* Split Data */}
            <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8 px-8">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                     <span className="p-1.5 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                        <HiOutlineClipboardList className="text-[18px]" />
                     </span>
                     <h4 className="text-[13px] font-black text-gray-400 uppercase tracking-widest">Información General</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                    <DataRow label="ID Envío" value={sg.id} isFirst />
                    <DataRow label="Nro. Orden" value={sg?.orderId} />
                    <DataRow 
                       label="Estado Actual" 
                       value={stateTitle} 
                       highlight 
                       highlightColor="bg-blue-600" 
                    />
                    <DataRow label="Canal de Venta" value={sg?.channel} />
                    <DataRow 
                       label="Portabilidad" 
                       value={sg?.orderType === "Portabilidad" ? "Si" : "No"} 
                       isLast
                    />
                  </div>
                </div>

                <div className="space-y-4">
                   <h4 className="text-[13px] font-black text-gray-400 uppercase tracking-widest pl-2">Localización</h4>
                   <CollapseWithDetail title="Origen (Source)" details={sg?.source} />
                   <CollapseWithDetail title="Destino (Target)" details={sg?.target} />
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                     <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100">
                        <HiOutlineCube className="text-[18px]" />
                     </span>
                     <h4 className="text-[13px] font-black text-gray-400 uppercase tracking-widest">Logística y Tiempos</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                    <DataRow label="Creación" value={`${format_date(sg?.orderCreationDate)} ${format_time(sg?.orderCreationDate)}`} isFirst />
                    <DataRow label="Operador / Courier" value={courierName || "-"} />
                    <DataRow label="Método Entrega" value={sg?.deliveryType || "-"} />
                    <DataRow label="ID Canal OSRM" value={sg?.salesChannelId || "-"} />
                    <DataRow 
                       label="Entrega Estimada" 
                       value={format_date(sg?.estimatedDeliveryDate)} 
                       highlight 
                       highlightColor="bg-emerald-500"
                       isLast
                    />
                  </div>
                </div>

                <div className="w-full flex flex-col gap-3">
                  <h4 className="text-[13px] font-black text-gray-400 uppercase tracking-widest pl-2">Documentos Adjuntos</h4>
                  <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.values(_docs).some((doc: any) => doc?.includes("http")) ? (
                      <>
                        {_docs._dte && (
                          <AttachmentCard href={_docs._dte} label="Factura / DTE" type="PDF" color="blue" />
                        )}
                        {_docs._labels && (
                          <AttachmentCard href={_docs._labels} label="Etiqueta Courier" type="LABEL" color="emerald" />
                        )}
                        {_docs._summary && (
                          <AttachmentCard href={_docs._summary} label="Manifiesto Carga" type="DOC" color="blue" />
                        )}
                      </>
                    ) : (
                      <div className="col-span-full py-8 text-center bg-gray-50/50 rounded-[20px] border border-dashed border-gray-200 text-gray-400 italic text-sm font-medium">
                        No hay documentos adjuntos en este envío.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper Components
const DataRow = ({ label, value, isFirst = false, isLast = false, highlight = false, highlightColor = "bg-blue-600" }: any) => (
  <div className={cn(
    "flex items-center justify-between px-5 py-3.5 transition-colors hover:bg-gray-50/50",
    !isLast && "border-b border-gray-50"
  )}>
    <span className="text-[13px] font-bold text-gray-400 uppercase tracking-tight">{label}</span>
    {highlight ? (
      <span className={cn("px-3 py-1 rounded-full text-white text-[12px] font-black uppercase tracking-widest shadow-sm", highlightColor)}>
        {value || "-"}
      </span>
    ) : (
      <span className="text-[15px] font-black text-gray-800">{value || "-"}</span>
    )}
  </div>
)

const AttachmentCard = ({ href, label, type, color = "blue" }: any) => {
    const isEmerald = color === "emerald"
    return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className={cn(
            "flex items-center gap-4 p-4 rounded-[20px] border transition-all hover:shadow-md group",
            isEmerald 
                ? "bg-emerald-50/20 border-emerald-100 hover:border-emerald-300" 
                : "bg-blue-50/20 border-blue-100 hover:border-blue-300"
          )}
        >
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center text-lg shadow-sm transition-transform group-hover:scale-110",
            isEmerald ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"
          )}>
             {type === "PDF" && <HiOutlineNewspaper />}
             {type === "LABEL" && <HiOutlineTicket />}
             {type === "DOC" && <HiOutlineDocumentText />}
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{type}</span>
            <span className={cn("text-[13px] font-black", isEmerald ? "text-emerald-700" : "text-blue-700")}>{label}</span>
          </div>
        </a>
    )
}

export default SgModalView
